// components/use-case-details/use-case-workflow/use-case-workflow-agent-card-component.tsx
// components/use-case-details/use-case-workflow-agent-card-component.tsx
"use client";

import { motion } from 'framer-motion';
import { Brain, Cpu, ExternalLink,Thermometer, Zap } from 'lucide-react';
import Link from 'next/link';
import React from 'react';

import { AgentType, agentTypeConfig, getAgentBorderColor } from './agent-type-config';

interface AgentCardProps {
  title: string;
  description: string;
  model: string;
  modelProvider?: string;
  modelSlug?: string;
  modelType?: string;
  temperature: string | number;
  trigger: string;
  bulletPoints: string | string[];
  output: string;
  borderColor?: string;
  topBorderColor?: string;
  agentType?: AgentType;
  className?: string;
}

const AgentCard: React.FC<AgentCardProps> = ({
  title,
  description,
  model,
  modelProvider = "Unknown Provider",
  modelSlug,
  modelType = "Reasoning",
  temperature,
  trigger,
  bulletPoints,
  output,
  borderColor,
  topBorderColor,
  agentType,
  className = "",
}) => {
  // Determine the border color from agentType if provided, otherwise use the passed borderColor
  const actualBorderColor = agentType 
    ? getAgentBorderColor(agentType)
    : (topBorderColor || borderColor || "bg-blue-600");
  
  // Get the icon for the agent type
  const agentIcon = agentType ? agentTypeConfig[agentType]?.icon : null;

  // Function to extract model family and version if not provided
  const getModelFamily = () => {
    if (model.includes("GPT")) return "GPT";
    if (model.includes("Claude")) return "Claude";
    if (model.includes("Gemini")) return "Gemini";
    if (model.includes("Llama")) return "Llama";
    return "";
  };

  // Generate model URL for linking
  const getModelUrl = () => {
    if (modelSlug) return `/models/${modelSlug}`;
    // If no slug provided, try to generate one from the model name
    const slug = model.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
    return `/models/${slug}`;
  };
  
  return (
    <motion.div
      className={`relative bg-gray-800/50 rounded-lg p-6 shadow-lg border-b border-r border-l border-gray-700 mb-8 overflow-hidden h-full flex flex-col min-h-[550px] ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
      whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
    >
      {/* Top border as a separate element for cleaner styling */}
      <div className={`absolute top-0 left-0 right-0 h-2 ${actualBorderColor}`} />
      
      {/* Card content with better spacing */}
      <div className="pt-4 flex flex-col h-full">
        <div className="flex items-start mb-4">
          {agentType && agentIcon && (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 bg-${agentTypeConfig[agentType].color}-600`}>
              {React.createElement(agentIcon, { size: 20, className: "text-white" })}
            </div>
          )}
          <div className="flex flex-col">
            <h4 className="text-xl font-bold text-white">{title}</h4>
            <span className="text-md text-gray-400 capitalize">{agentType}</span>
          </div>
        </div>

        <div className="mb-6 flex-grow">
          <p className="mb-4 leading-relaxed text-lg text-white">{description}</p>
          {Array.isArray(bulletPoints) && bulletPoints.length > 0 && (
            <ul className="list-disc list-inside text-gray-300/90 space-y-2 ml-2 text-base">
              {bulletPoints.map((item, idx) => (
                <li key={idx} className="leading-relaxed">{item}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Tech specs box with improved styling - now at the bottom */}
        <div className="mt-auto">
          <div className="bg-black rounded-lg p-4 mb-5 shadow-inner">
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Model section with provider and model name */}
              <div className="flex flex-col items-center text-center">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium flex items-center gap-1 mb-1">
                  <Cpu size={14} className="text-gray-400" /> MODEL PROVIDER
                </span>
                <Link 
                  href={`/providers/${modelProvider.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-sm text-white font-medium hover:text-blue-400 transition-colors"
                >
                  {modelProvider}
                </Link>
                <Link 
                  href={getModelUrl()}
                  className="text-xs text-blue-400 flex items-center gap-1 mt-1 hover:underline"
                >
                  <span>{model}</span>
                  <ExternalLink size={10} />
                </Link>
              </div>
              
              {/* Model Type section */}
              <div className="flex flex-col items-center text-center">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium flex items-center gap-1 mb-1">
                  <Brain size={14} className="text-gray-400" /> MODEL TYPE
                </span>
                <p className="text-sm text-white font-medium">{modelType}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Temperature section */}
              <div className="flex flex-col items-center text-center">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium flex items-center gap-1 mb-1">
                  <Thermometer size={14} className="text-gray-400" /> TEMPERATURE
                </span>
                <p className="text-sm text-white font-medium">{temperature}</p>
              </div>
              
              {/* Trigger section with improved handling for multi-line text */}
              <div className="flex flex-col items-center text-center h-full">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium flex items-center gap-1 mb-1">
                  <Zap size={14} className="text-gray-400" /> TRIGGERED BY
                </span>
                <p className="text-sm text-white font-medium text-balance min-h-[2.5em] flex items-center justify-center">{trigger}</p>
              </div>
            </div>
          </div>

          {/* Input/Output footer with improved styling */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 text-sm pt-4 border-t border-gray-700">
            <div className="flex-1">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-medium block mb-1">Input</span>
              <p className="font-medium text-base">{typeof bulletPoints === 'string' ? bulletPoints : 'See above'}</p>
            </div>
            <div className="flex-1 sm:text-right">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-medium block mb-1">Output</span>
              <p className="font-medium text-base">{output}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AgentCard;